import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";

export default function EditParticipatingCountries({
  scenathonId,
  scenathonName,
  onSaved,
}) {
  const { authFetch } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allCountries, setAllCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [error, setError] = useState(null);

  // Load countries on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load all available countries
        const countriesResponse = await authFetch("/api/scenathon/country-labels");
        if (!countriesResponse.ok) {
          throw new Error("Error loading countries");
        }
        const countriesData = await countriesResponse.json();
        setAllCountries(countriesData.countries || []);

        // Load currently participating countries for this scenathon
        const participatingResponse = await authFetch(
          `/api/scenathon/scenathons/${scenathonId}/participating-countries`
        );
        if (!participatingResponse.ok) {
          throw new Error("Error loading participating countries");
        }
        const participatingData = await participatingResponse.json();
        const participatingCodes = participatingData.countries.map(
          (c) => c.country_code
        );
        setSelectedCountries(participatingCodes);
      } catch (err) {
        console.error("Load error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [scenathonId, authFetch]);

  const handleCountryToggle = (countryCode) => {
    setSelectedCountries((prev) => {
      if (prev.includes(countryCode)) {
        return prev.filter((c) => c !== countryCode);
      } else {
        return [...prev, countryCode];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCountries.length === allCountries.length) {
      setSelectedCountries([]);
    } else {
      setSelectedCountries(allCountries.map((c) => c.country_code));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const response = await authFetch(
        `/api/scenathon/scenathons/${scenathonId}/participating-countries`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ countryCodes: selectedCountries }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Error saving participating countries");
      }

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: `Participating countries updated successfully (${selectedCountries.length} selected)`,
        timer: 1500,
        showConfirmButton: false,
      });

      if (onSaved) {
        onSaved();
      }
    } catch (err) {
      console.error("Save error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading countries...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700 font-medium">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">
          Participating Countries - {scenathonName}
        </h3>
        <p className="text-sm text-slate-600">
          Select which countries will participate in this scenathon
        </p>
      </div>

      <div>
        <button
          type="button"
          onClick={handleSelectAll}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm transition-colors"
        >
          {selectedCountries.length === allCountries.length && allCountries.length > 0
            ? "Deselect All"
            : "Select All"}
        </button>
      </div>

      {allCountries.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 bg-slate-50 rounded-lg max-h-96 overflow-y-auto border border-slate-200">
          {allCountries.map((country) => (
            <label
              key={country.country_code}
              className="flex items-center cursor-pointer hover:bg-white p-2 rounded transition-colors"
            >
              <input
                type="checkbox"
                checked={selectedCountries.includes(country.country_code)}
                onChange={() => handleCountryToggle(country.country_code)}
                disabled={saving}
                className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700 truncate font-medium">
                {country.country_label}
              </span>
              <span className="ml-1 text-xs text-gray-500">
                ({country.country_code})
              </span>
            </label>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 italic p-4 bg-slate-50 rounded-lg">
          No countries available
        </p>
      )}

      {selectedCountries.length > 0 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>{selectedCountries.length}</strong> country/countries selected
          </p>
        </div>
      )}

      <div className="flex gap-2 pt-4 border-t">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium transition-colors"
        >
          {saving ? "Saving..." : "Save Countries"}
        </button>
      </div>
    </div>
  );
}
